/**
 * CHAT INTERFACE - REVOLUTIONARY AI CONVERSATION
 * Advanced chat interface with real-time AI responses
 */

import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Mic, Code, Sparkles, Brain, Zap } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';

// Components
import { CodeBlock } from './CodeBlock';
import { TypingIndicator } from './TypingIndicator';
import { MessageSuggestions } from './MessageSuggestions';

// Hooks
import { useChatHistory } from '../hooks/useChatHistory';
import { useAIService } from '../hooks/useAIService';

// Types
interface Message {
  id: string;
  type: 'user' | 'ai' | 'system';
  content: string;
  timestamp: number;
  codeBlocks?: Array<{
    language: string;
    code: string;
  }>;
  suggestions?: string[];
  processingTime?: number;
}

interface ChatInterfaceProps {
  sessionId: string;
  sendMessage: (message: any) => void;
}

export function ChatInterface({ sessionId, sendMessage }: ChatInterfaceProps) {
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isVoiceMode, setIsVoiceMode] = useState(false);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  
  // Hooks
  const { messages, addMessage, clearHistory } = useChatHistory(sessionId);
  const { sendChatMessage, isLoading } = useAIService();

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: 'user',
      content: input.trim(),
      timestamp: Date.now()
    };

    // Add user message
    addMessage(userMessage);
    setInput('');
    setIsTyping(true);

    try {
      // Send to AI service
      const response = await sendChatMessage({
        message: input.trim(),
        sessionId,
        context: {
          recentMessages: messages.slice(-5) // Last 5 messages for context
        }
      });

      if (response.response) {
        // Add AI response
        const aiMessage: Message = {
          id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          type: 'ai',
          content: response.response,
          timestamp: Date.now(),
          codeBlocks: response.codeBlocks,
          suggestions: response.suggestions,
          processingTime: response.processingTime
        };

        addMessage(aiMessage);
      }

      // Send via WebSocket for real-time features
      sendMessage({
        type: 'chat_message',
        content: input.trim(),
        sessionId,
        timestamp: Date.now()
      });

    } catch (error) {
      console.error('Chat error:', error);
      
      // Add error message
      const errorMessage: Message = {
        id: `msg_${Date.now()}_error`,
        type: 'system',
        content: 'Please deploy to Cloudflare to use real Llama 3.3 AI. No simulations available. Run: wrangler deploy',
        timestamp: Date.now()
      };
      
      addMessage(errorMessage);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInput(suggestion);
    inputRef.current?.focus();
  };

  const welcomeMessage = messages.length === 0;

  return (
    <div className="flex flex-col h-full bg-gradient-to-b from-slate-900/50 to-slate-800/50 backdrop-blur-sm">
      {/* Chat Header */}
      <div className="border-b border-white/10 p-4 bg-white/5 backdrop-blur-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="relative">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                <Brain className="w-5 h-5 text-white" />
              </div>
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-slate-900 animate-pulse" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">CodeMind AI</h2>
              <p className="text-sm text-slate-400">Your AI Development Partner</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsVoiceMode(!isVoiceMode)}
              className={`p-2 rounded-lg transition-colors ${
                isVoiceMode 
                  ? 'bg-red-500 text-white' 
                  : 'bg-white/10 text-slate-300 hover:bg-white/20'
              }`}
            >
              <Mic className="w-4 h-4" />
            </button>
            
            <button
              onClick={clearHistory}
              className="p-2 rounded-lg bg-white/10 text-slate-300 hover:bg-white/20 transition-colors"
            >
              Clear Chat
            </button>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {welcomeMessage && (
          <WelcomeScreen onSuggestionClick={handleSuggestionClick} />
        )}

        <AnimatePresence>
          {messages.map((message) => (
            <MessageBubble
              key={message.id}
              message={message}
              onSuggestionClick={handleSuggestionClick}
            />
          ))}
        </AnimatePresence>

        {isTyping && <TypingIndicator />}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="border-t border-white/10 p-4 bg-white/5 backdrop-blur-sm">
        <div className="flex items-end space-x-3">
          <div className="flex-1 relative">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask me anything about code, architecture, or development..."
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-slate-400 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={input.split('\n').length}
              maxLength={2000}
              disabled={isLoading}
            />
            
            <div className="absolute bottom-2 right-2 text-xs text-slate-500">
              {input.length}/2000
            </div>
          </div>
          
          <button
            onClick={handleSendMessage}
            disabled={!input.trim() || isLoading}
            className="p-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105"
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Welcome Screen Component
interface WelcomeScreenProps {
  onSuggestionClick: (suggestion: string) => void;
}

function WelcomeScreen({ onSuggestionClick }: WelcomeScreenProps) {
  const suggestions = [
    "Review this React component for performance issues",
    "Generate a TypeScript interface for user data",
    "Explain the difference between async/await and Promises",
    "Create a secure authentication system",
    "Optimize this SQL query for better performance",
    "Design a microservices architecture"
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center py-12"
    >
      <div className="mb-8">
        <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
          <Sparkles className="w-10 h-10 text-white" />
        </div>
        <h1 className="text-3xl font-bold text-white mb-2">Welcome to CodeMind AI</h1>
        <p className="text-slate-400 text-lg">Your revolutionary AI-powered development assistant</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-4xl mx-auto">
        {suggestions.map((suggestion, index) => (
          <motion.button
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            onClick={() => onSuggestionClick(suggestion)}
            className="p-4 bg-white/10 border border-white/20 rounded-lg text-left text-white hover:bg-white/20 transition-colors group"
          >
            <div className="flex items-start space-x-3">
              <Code className="w-5 h-5 text-blue-400 mt-0.5 group-hover:text-blue-300" />
              <span className="text-sm">{suggestion}</span>
            </div>
          </motion.button>
        ))}
      </div>
    </motion.div>
  );
}

// Message Bubble Component
interface MessageBubbleProps {
  message: Message;
  onSuggestionClick: (suggestion: string) => void;
}

function MessageBubble({ message, onSuggestionClick }: MessageBubbleProps) {
  const isUser = message.type === 'user';
  const isSystem = message.type === 'system';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
    >
      <div className={`max-w-4xl ${isUser ? 'order-2' : 'order-1'}`}>
        {/* Avatar */}
        <div className={`flex items-start space-x-3 ${isUser ? 'flex-row-reverse space-x-reverse' : ''}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
            isUser 
              ? 'bg-gradient-to-r from-green-500 to-teal-600' 
              : isSystem
              ? 'bg-gradient-to-r from-orange-500 to-red-600'
              : 'bg-gradient-to-r from-blue-500 to-purple-600'
          }`}>
            {isUser ? (
              <span className="text-white text-sm font-semibold">U</span>
            ) : isSystem ? (
              <Zap className="w-4 h-4 text-white" />
            ) : (
              <Brain className="w-4 h-4 text-white" />
            )}
          </div>

          {/* Message Content */}
          <div className={`flex-1 ${isUser ? 'text-right' : 'text-left'}`}>
            <div className={`inline-block p-4 rounded-lg ${
              isUser 
                ? 'bg-gradient-to-r from-green-500 to-teal-600 text-white' 
                : isSystem
                ? 'bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/30 text-orange-200'
                : 'bg-white/10 border border-white/20 text-white'
            }`}>
              {/* Message Text */}
              <div className="prose prose-invert max-w-none">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeHighlight]}
                  components={{
                    code: ({ className, children, ...props }: any) => {
                      const match = /language-(\w+)/.exec(className || '');
                      return match ? (
                        <CodeBlock
                          language={match[1]}
                          code={String(children).replace(/\n$/, '')}
                        />
                      ) : (
                        <code className="bg-black/30 px-1 py-0.5 rounded text-sm" {...props}>
                          {children}
                        </code>
                      );
                    }
                  }}
                >
                  {message.content}
                </ReactMarkdown>
              </div>

              {/* Code Blocks */}
              {message.codeBlocks && message.codeBlocks.length > 0 && (
                <div className="mt-4 space-y-3">
                  {message.codeBlocks.map((block, index) => (
                    <CodeBlock
                      key={index}
                      language={block.language}
                      code={block.code}
                    />
                  ))}
                </div>
              )}

              {/* Processing Time */}
              {message.processingTime && (
                <div className="mt-2 text-xs opacity-70">
                  Processed in {message.processingTime}ms
                </div>
              )}
            </div>

            {/* Suggestions */}
            {message.suggestions && message.suggestions.length > 0 && (
              <MessageSuggestions
                suggestions={message.suggestions}
                onSuggestionClick={onSuggestionClick}
              />
            )}

            {/* Timestamp */}
            <div className={`text-xs text-slate-500 mt-1 ${isUser ? 'text-right' : 'text-left'}`}>
              {new Date(message.timestamp).toLocaleTimeString()}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default ChatInterface;